import { useWindowDomEvent } from 'hooks/use-dom-event'
import * as React from 'react'
import {
  defaultFloatingPanelPosition,
  floatingPanelMaxHeight,
} from '../utils/clamp-floating-position'
import { observeStudioLayoutChrome } from '../utils/observe-studio-layout-chrome'
import { readFloatingPanelViewport } from '../utils/read-floating-panel-viewport'
import { setAidaFloatingPosition, useAidaOverlayStore } from '../stores/overlay-store'
import type { AidaFloatingPosition } from '../stores/overlay-store'

const KEYBOARD_NUDGE: Record<string, [number, number]> = {
  ArrowLeft: [-1, 0],
  ArrowRight: [1, 0],
  ArrowUp: [0, -1],
  ArrowDown: [0, 1],
}

const KEYBOARD_NUDGE_STEP_PX = 10
const KEYBOARD_NUDGE_SHIFT_STEP_PX = 40

interface DragSession {
  pointer: number
  target: HTMLButtonElement
  x: number
  y: number
  origin: AidaFloatingPosition
}

export type FloatingPanelDragHandle = ReturnType<typeof useFloatingPanelDragHandle>

const dragSession: { current: DragSession | null } = { current: null }

function resolvedFloatingPosition(): AidaFloatingPosition {
  return (
    useAidaOverlayStore.getState().floatingPosition ??
    defaultFloatingPanelPosition(readFloatingPanelViewport())
  )
}

function releaseDragCapture(session: DragSession | null) {
  if (session?.target.hasPointerCapture(session.pointer)) {
    session.target.releasePointerCapture(session.pointer)
  }
}

function startDragSession(
  drag: React.RefObject<DragSession | null>,
  event: React.PointerEvent<HTMLButtonElement>,
) {
  if (event.button !== 0) {
    return
  }
  event.currentTarget.setPointerCapture(event.pointerId)
  drag.current = {
    pointer: event.pointerId,
    target: event.currentTarget,
    x: event.clientX,
    y: event.clientY,
    origin: resolvedFloatingPosition(),
  }
}

function moveDragSession(
  drag: React.RefObject<DragSession | null>,
  event: React.PointerEvent<HTMLButtonElement>,
) {
  const start = drag.current
  if (start?.pointer !== event.pointerId) {
    return
  }
  setAidaFloatingPosition({
    x: start.origin.x + event.clientX - start.x,
    y: start.origin.y + event.clientY - start.y,
  })
}

function finishDragSession(
  drag: React.RefObject<DragSession | null>,
  event: React.PointerEvent<HTMLButtonElement>,
) {
  if (drag.current?.pointer !== event.pointerId) {
    return
  }
  drag.current = null
  if (event.currentTarget.hasPointerCapture(event.pointerId)) {
    event.currentTarget.releasePointerCapture(event.pointerId)
  }
}

function nudgeFloatingPanel(event: React.KeyboardEvent<HTMLButtonElement>) {
  const delta = KEYBOARD_NUDGE[event.key]
  if (!delta) {
    return
  }
  event.preventDefault()
  const current = resolvedFloatingPosition()
  const step = event.shiftKey ? KEYBOARD_NUDGE_SHIFT_STEP_PX : KEYBOARD_NUDGE_STEP_PX
  setAidaFloatingPosition({
    x: current.x + delta[0] * step,
    y: current.y + delta[1] * step,
  })
}

function useFloatingPanelDragHandle(drag: { current: DragSession | null }) {
  const onPointerDown = React.useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      startDragSession(drag, event)
    },
    [drag],
  )

  const onPointerMove = React.useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      moveDragSession(drag, event)
    },
    [drag],
  )

  const onPointerEnd = React.useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      finishDragSession(drag, event)
    },
    [drag],
  )

  const onKeyDown = React.useCallback((event: React.KeyboardEvent<HTMLButtonElement>) => {
    nudgeFloatingPanel(event)
  }, [])

  return React.useMemo(
    () => ({
      onPointerDown,
      onPointerMove,
      onPointerUp: onPointerEnd,
      onPointerCancel: onPointerEnd,
      onLostPointerCapture: onPointerEnd,
      onKeyDown,
    }),
    [onKeyDown, onPointerDown, onPointerEnd, onPointerMove],
  )
}

export function useFloatingDragHandle() {
  return useFloatingPanelDragHandle(dragSession)
}

export function useFloatingPanelPosition(enabled: boolean) {
  const storedPosition = useAidaOverlayStore(state => state.floatingPosition)

  const cancelDrag = React.useCallback(() => {
    releaseDragCapture(dragSession.current)
    dragSession.current = null
  }, [])

  const reset = React.useCallback(() => {
    cancelDrag()
    setAidaFloatingPosition(defaultFloatingPanelPosition(readFloatingPanelViewport()))
  }, [cancelDrag])

  const reclampOnResize = React.useCallback(() => {
    cancelDrag()
    const current = useAidaOverlayStore.getState().floatingPosition
    if (current) {
      setAidaFloatingPosition(current)
    }
  }, [cancelDrag])

  useWindowDomEvent('resize', enabled ? reclampOnResize : false)
  useWindowDomEvent('blur', enabled ? cancelDrag : false)

  React.useEffect(() => {
    if (!enabled) {
      return
    }
    return observeStudioLayoutChrome(reclampOnResize)
  }, [enabled, reclampOnResize])

  React.useEffect(() => {
    if (!enabled) {
      cancelDrag()
    }
  }, [cancelDrag, enabled])

  const viewport = readFloatingPanelViewport()
  const position = storedPosition ?? defaultFloatingPanelPosition(viewport)
  const handle = useFloatingPanelDragHandle(dragSession)

  return { position, height: floatingPanelMaxHeight(viewport), reset, handle }
}
